import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Bug, 
  Zap, 
  Eye, 
  Shield, 
  Code, 
  BarChart3, 
  Layers, 
  ArrowRight, 
  Check,
  Terminal,
  Sparkles,
  Clock,
  Target,
  Lightbulb,
  GitBranch,
  Cpu,
  ChevronRight,
  LayoutDashboard,
  LogOut
} from "lucide-react";

const codeExamples = {
  basic: `import RAGDebugger from 'rag-debugger-sdk';

const debugger = new RAGDebugger({
  apiKey: 'your-api-key',
  projectId: 'your-project-id',
  environment: 'production'
});

// Send a trace after each RAG call
await debugger.trace({
  userQuery: "What is quantum computing?",
  retrievedChunks: chunks,
  constructedPrompt: prompt,
  modelResponse: response,
  modelName: "gpt-4",
  latencyMs: 1200
});`,
  wrapper: `// Wrap your existing functions - zero code changes
const wrappedRetriever = debugger.wrapRetriever(
  yourRetrieverFunction
);

const wrappedLLM = debugger.wrapLLM(
  yourLLMFunction,
  { modelName: 'gpt-4' }
);

// Use wrapped functions as normal
const chunks = await wrappedRetriever(query);
const response = await wrappedLLM(query, chunks, prompt);
// Traces are sent automatically!`,
  config: `const debugger = new RAGDebugger({
  apiKey: process.env.RAG_DEBUGGER_KEY,
  projectId: 'my-rag-app',
  environment: 'production',
  
  // Sampling for high-traffic apps
  sampleRate: 0.1, // 10% of requests
  
  // Privacy controls
  maskSensitiveData: true,
  
  // Custom metadata
  metadata: {
    version: '2.1.0',
    region: 'us-east-1'
  }
});`
};

const features = [
  {
    icon: Eye,
    title: "See What Happened",
    description: "Full visibility into every step: user query, retrieved chunks, constructed prompt, and model response."
  },
  {
    icon: Lightbulb,
    title: "Understand Why It Failed",
    description: "AI-powered analysis explains failures in plain English. No more guessing why your RAG gave wrong answers."
  },
  {
    icon: Target,
    title: "Know How to Fix It",
    description: "Actionable suggestions: adjust chunk size, increase similarity threshold, use hybrid search."
  },
  {
    icon: Sparkles,
    title: "Hallucination Detection",
    description: "Automatically highlights response text not grounded in retrieved context."
  },
  {
    icon: BarChart3,
    title: "Retrieval Confidence",
    description: "Visual charts showing chunk relevance scores. Spot weak retrievals instantly."
  },
  {
    icon: Zap,
    title: "Real-time Monitoring",
    description: "WebSocket-powered live updates. See traces as they happen across environments."
  }
];

const integrationSteps = [
  { step: "01", title: "Install SDK", code: "npm install rag-debugger-sdk" },
  { step: "02", title: "Initialize", code: "const debugger = new RAGDebugger({ apiKey, projectId })" },
  { step: "03", title: "Trace", code: "await debugger.trace({ userQuery, chunks, response })" },
  { step: "04", title: "Debug", code: "Open dashboard → See insights" }
];

export default function LandingPage() {
  const [activeTab, setActiveTab] = useState("basic");
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation */}
      <nav className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-sm bg-foreground flex items-center justify-center">
              <Bug className="w-5 h-5 text-background" />
            </div>
            <span className="font-black text-xl tracking-tight font-heading">RAG Debugger</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              About
            </Link>
            {user ? (
              <>
                <Button variant="ghost" onClick={logout} data-testid="nav-signout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
                <Link to="/dashboard">
                  <Button data-testid="nav-dashboard-button">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/auth">
                  <Button variant="ghost" data-testid="nav-signin-button">Sign In</Button>
                </Link>
                <Link to="/auth">
                  <Button data-testid="nav-getstarted-button">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-100 via-background to-background" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-32">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-sm font-medium">
              <Sparkles className="w-3.5 h-3.5 mr-2" />
              AI-Powered RAG Pipeline Debugging
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight font-heading leading-[1.1] mb-6">
              Finally understand why your{" "}
              <span className="relative">
                <span className="relative z-10">RAG pipeline</span>
                <span className="absolute bottom-2 left-0 right-0 h-3 bg-yellow-300/50 -z-0" />
              </span>{" "}
              is failing
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              Deep observability for Retrieval-Augmented Generation. See what happened, 
              understand why it failed, know how to fix it — in seconds.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              {user ? (
                <Link to="/dashboard">
                  <Button size="lg" className="h-12 px-8 text-base" data-testid="hero-dashboard-button">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button size="lg" className="h-12 px-8 text-base" data-testid="hero-getstarted-button">
                    Start Debugging Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
              <a href="#sdk">
                <Button size="lg" variant="outline" className="h-12 px-8 text-base" data-testid="hero-viewsdk-button">
                  <Code className="w-4 h-4 mr-2" />
                  View SDK
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black font-mono">10m</div>
                <div className="text-sm text-muted-foreground mt-1">Integration</div>
              </div>
              <div className="text-center border-x border-border">
                <div className="text-3xl sm:text-4xl font-black font-mono">&lt;1s</div>
                <div className="text-sm text-muted-foreground mt-1">Latency Impact</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black font-mono">AI</div>
                <div className="text-sm text-muted-foreground mt-1">Analysis</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution */}
      <section className="border-y border-border bg-muted/30 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight font-heading mb-6">
                RAG debugging shouldn't be a nightmare
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">✗</span>
                  Your chatbot gives wrong answers and you don't know why
                </p>
                <p className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">✗</span>
                  Was it bad retrieval? Wrong chunks? Hallucination?
                </p>
                <p className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">✗</span>
                  Hours of logging, print statements, and guesswork
                </p>
              </div>
            </div>
            <div className="bg-background rounded-sm border border-border p-6 lg:p-8">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                With RAG Debugger
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-sm border border-green-100">
                  <Eye className="w-5 h-5 text-green-600" />
                  <span className="text-sm">See every chunk retrieved and its relevance score</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-sm border border-green-100">
                  <Lightbulb className="w-5 h-5 text-green-600" />
                  <span className="text-sm">"This failed because chunk #2 was irrelevant (score: 0.32)"</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-sm border border-green-100">
                  <Target className="w-5 h-5 text-green-600" />
                  <span className="text-sm">"Fix: Increase similarity threshold to 0.7"</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight font-heading mb-4">
              Everything you need to debug RAG
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From trace collection to intelligent analysis, we've got you covered.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className="p-6 border border-border rounded-sm hover:border-foreground/30 transition-colors"
                data-testid={`feature-card-${idx}`}
              >
                <feature.icon className="w-10 h-10 mb-4 text-foreground" strokeWidth={1.5} />
                <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="py-16 lg:py-20 bg-muted/30 border-y border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight font-heading mb-4">
              See it in action — 5 min demo
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Watch a real integration from zero to a working dashboard: sign up, create a project, drop in the SDK, and start seeing what's actually happening inside your pipeline.
            </p>
          </div>
          <div className="relative rounded-sm border border-border overflow-hidden shadow-lg" style={{ padding: "56.25% 0 0 0", position: "relative" }} data-testid="demo-video">
            <iframe
              src="https://www.loom.com/embed/ab0b8b5094104cdeb6edf8f41d156381"
              frameBorder="0"
              allowFullScreen
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              title="RAG Debugger Demo"
            />
          </div>
          <div className="text-center mt-10">
            <p className="text-base text-muted-foreground max-w-2xl mx-auto mb-2">
              It takes less than 10 minutes to integrate. No credit card. No enterprise sales call.
            </p>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto mb-6">
              If you're building with LangChain, LlamaIndex, or any Python-based RAG stack — you can have this running before your next standup.
            </p>
            <Link to="/auth">
              <Button size="lg" className="h-12 px-8 text-base" data-testid="demo-cta-button">
                Get started free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SDK Integration Section */}
      <section id="sdk" className="py-20 lg:py-28 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-6 border-slate-700 text-slate-300">
              <Terminal className="w-3.5 h-3.5 mr-2" />
              Developer-First SDK
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight font-heading mb-4">
              Integrate in minutes, not days
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Lightweight SDK with zero dependencies on your RAG framework. Works with LangChain, LlamaIndex, or custom pipelines.
            </p>
          </div>

          {/* Quick Steps */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {integrationSteps.map((item, idx) => (
              <div key={idx} className="bg-slate-900 border border-slate-800 rounded-sm p-4">
                <div className="text-xs font-bold text-slate-500 mb-2">{item.step}</div>
                <div className="text-sm font-semibold mb-2">{item.title}</div>
                <code className="text-xs text-slate-400 font-mono">{item.code}</code>
              </div>
            ))}
          </div>

          {/* Code Examples */}
          <div className="bg-slate-900 rounded-sm border border-slate-800 overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="border-b border-slate-800 px-4">
                <TabsList className="bg-transparent h-12 gap-4">
                  <TabsTrigger 
                    value="basic" 
                    className="data-[state=active]:bg-transparent data-[state=active]:text-white text-slate-400 data-[state=active]:border-b-2 data-[state=active]:border-white rounded-none px-0 pb-3"
                  >
                    Basic Usage
                  </TabsTrigger>
                  <TabsTrigger 
                    value="wrapper"
                    className="data-[state=active]:bg-transparent data-[state=active]:text-white text-slate-400 data-[state=active]:border-b-2 data-[state=active]:border-white rounded-none px-0 pb-3"
                  >
                    Function Wrappers
                  </TabsTrigger>
                  <TabsTrigger 
                    value="config"
                    className="data-[state=active]:bg-transparent data-[state=active]:text-white text-slate-400 data-[state=active]:border-b-2 data-[state=active]:border-white rounded-none px-0 pb-3"
                  >
                    Advanced Config
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="basic" className="m-0">
                <pre className="p-6 text-sm font-mono text-slate-300 overflow-x-auto">
                  <code>{codeExamples.basic}</code>
                </pre>
              </TabsContent>
              <TabsContent value="wrapper" className="m-0">
                <pre className="p-6 text-sm font-mono text-slate-300 overflow-x-auto">
                  <code>{codeExamples.wrapper}</code>
                </pre>
              </TabsContent>
              <TabsContent value="config" className="m-0">
                <pre className="p-6 text-sm font-mono text-slate-300 overflow-x-auto">
                  <code>{codeExamples.config}</code>
                </pre>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Customization Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight font-heading mb-4">
              Fully customizable for your workflow
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Configure sampling, privacy controls, environments, and more.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 border border-border rounded-sm">
              <GitBranch className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-2">Multi-Environment</h3>
              <p className="text-sm text-muted-foreground mb-4">Track traces across dev, staging, and production. Filter and compare.</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">environment: 'production'</code>
            </div>
            <div className="p-6 border border-border rounded-sm">
              <Clock className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-2">Smart Sampling</h3>
              <p className="text-sm text-muted-foreground mb-4">High-traffic apps? Sample 10% or 1% of requests. Zero overhead.</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">sampleRate: 0.1</code>
            </div>
            <div className="p-6 border border-border rounded-sm">
              <Shield className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-2">Privacy Controls</h3>
              <p className="text-sm text-muted-foreground mb-4">Auto-mask emails, phone numbers, and PII before sending.</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">maskSensitiveData: true</code>
            </div>
            <div className="p-6 border border-border rounded-sm">
              <Layers className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-2">Custom Metadata</h3>
              <p className="text-sm text-muted-foreground mb-4">Attach version, region, user ID, or any custom fields.</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">metadata: &#123; version: '2.0' &#125;</code>
            </div>
            <div className="p-6 border border-border rounded-sm">
              <Cpu className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-2">Framework Agnostic</h3>
              <p className="text-sm text-muted-foreground mb-4">Works with LangChain, LlamaIndex, Haystack, or custom code.</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">// Any framework!</code>
            </div>
            <div className="p-6 border border-border rounded-sm">
              <Zap className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-2">Async & Non-blocking</h3>
              <p className="text-sm text-muted-foreground mb-4">Traces are sent asynchronously. No latency added to your app.</p>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">// Fire and forget</code>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-foreground text-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight font-heading mb-6">
            Stop guessing. Start debugging.
          </h2>
          <p className="text-lg text-background/70 mb-10 max-w-2xl mx-auto">
            Join developers who finally understand why their RAG pipelines fail. 
            Free to start, scale as you grow.
          </p>
          {user ? (
            <Link to="/dashboard">
              <Button size="lg" variant="secondary" className="h-12 px-8 text-base" data-testid="cta-dashboard-button">
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/auth">
              <Button size="lg" variant="secondary" className="h-12 px-8 text-base" data-testid="cta-getstarted-button">
                Get Started Free
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-sm bg-foreground flex items-center justify-center">
                <Bug className="w-4 h-4 text-background" />
              </div>
              <span className="font-bold">RAG Debugger</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Built by <a href="https://vasanthnatarajan.me" target="_blank" rel="noopener noreferrer" className="hover:text-foreground underline">Vasanth Natarajan</a> for developers who demand answers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
